import React, { useState } from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Clock, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ackIncident } from "@/lib/dashboardApi";

export default function AttentionStrip({ incidents = [], onReload, token }) {
  const [loadingKey, setLoadingKey] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const activeIncidents = (incidents || []).filter(inc => inc.status !== "resolved");

  if (activeIncidents.length === 0) {
    return (
      <div className="flex items-center gap-snug rounded-lg border border-success/30 bg-success-subtle px-loose py-snug text-success">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        <span className="text-body font-medium">All systems operational — no active attention items.</span>
      </div>
    );
  }

  const handleAck = async (key) => {
    try {
      setLoadingKey(key);
      await ackIncident(key, token);
      if (onReload) onReload();
    } catch (err) {
      console.error("Failed to acknowledge incident:", err);
    } finally {
      setLoadingKey(null);
    }
  };

  const primary = activeIncidents[0];
  const others = activeIncidents.slice(1);

  const getSeverityStyle = (sev) => {
    switch (sev) {
      case "critical":
        return "border-danger/40 bg-danger-subtle text-danger";
      case "warning":
        return "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400";
      default:
        return "border-info/40 bg-info-subtle text-info";
    }
  };

  const getSeverityIcon = (sev) => {
    switch (sev) {
      case "critical":
        return <AlertCircle className="h-4 w-4 shrink-0 text-danger" />;
      case "warning":
        return <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />;
      default:
        return <Info className="h-4 w-4 shrink-0 text-info" />;
    }
  };

  return (
    <div className="space-y-snug" data-testid="dash-attention-strip">
      <div
        className={`flex flex-wrap items-center justify-between gap-snug rounded-lg border px-loose py-base transition-all ${getSeverityStyle(
          primary.severity
        )}`}
      >
        <div className="flex items-start gap-snug min-w-0">
          <div className="mt-0.5">{getSeverityIcon(primary.severity)}</div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-tight">
              <span className="font-semibold text-body capitalize">{primary.title || primary.incident_type.replace(/_/g, " ")}</span>
              {primary.occurrence_count > 1 && (
                <span className="rounded-full bg-background/80 px-tight py-hair text-[10px] font-bold uppercase tracking-wider text-foreground border">
                  x{primary.occurrence_count} occurrences
                </span>
              )}
              {primary.status === "acknowledged" && (
                <span className="rounded bg-muted px-tight py-hair text-[10px] font-medium text-muted-foreground">
                  Acknowledged
                </span>
              )}
            </div>
            <p className="mt-hair text-meta opacity-90">{primary.summary}</p>
          </div>
        </div>

        <div className="flex items-center gap-snug shrink-0">
          {primary.status === "active" && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 border-current hover:bg-background/20"
              disabled={loadingKey === primary.incident_key}
              onClick={() => handleAck(primary.incident_key)}
              data-testid={`ack-${primary.incident_key}`}
            >
              {loadingKey === primary.incident_key ? "Acknowledging…" : "Acknowledge"}
            </Button>
          )}

          {others.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 gap-tight px-snug text-meta"
              onClick={() => setExpanded(!expanded)}
            >
              <span>{others.length} more</span>
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </Button>
          )}
        </div>
      </div>

      {expanded && others.length > 0 && (
        <div className="space-y-tight pl-base">
          {others.map((inc) => (
            <div
              key={inc.incident_key}
              className={`flex flex-wrap items-center justify-between gap-snug rounded-md border p-snug ${getSeverityStyle(
                inc.severity
              )}`}
            >
              <div className="flex items-center gap-snug">
                {getSeverityIcon(inc.severity)}
                <span className="font-medium text-meta">{inc.summary}</span>
                {inc.occurrence_count > 1 && (
                  <span className="text-[10px] rounded bg-background/60 px-tight font-bold">x{inc.occurrence_count}</span>
                )}
              </div>
              {inc.status === "active" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-meta"
                  disabled={loadingKey === inc.incident_key}
                  onClick={() => handleAck(inc.incident_key)}
                >
                  {loadingKey === inc.incident_key ? "…" : "Ack"}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
